import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const marqueeItems = [
  'Contract intelligence', 'Measure calculation', 'Patient attribution',
  'Care gap detection', 'Payout reconciliation', 'Payer comparison',
  'Audit-grade reporting', 'HIPAA compliant',
];

const Home = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const doubled = [...marqueeItems, ...marqueeItems];

  return (
    <div className="cy-body">
      {/* HERO */}
      <section className="cy-hero">
        <div className="cy-hero-left">
          <div className="hero-label">Value-based care revenue intelligence</div>
          <h1 className="hero-h1">
            Stop leaving <span>millions</span> on the table.
          </h1>
          <p className="hero-sub">
            CareYield reads your value-based care contracts, computes real patient outcomes from your EMR, and calculates exactly how much incentive revenue you've earned — down to each contract clause.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="btn-primary">
              Request a demo
              <ArrowRight size={15} strokeWidth={2.5} />
            </Link>
            <Link to="/whitepaper" className="btn-ghost">
              Read the whitepaper
            </Link>
          </div>
        </div>

        <div className="cy-hero-right">
          <div className="orb-container">
            <div className="orb orb-ring2" />
            <div className="orb orb-ring" />
            <div className="orb orb-main" />
            <div className="orb orb-inner">
              <div className="orb-inner-text">
                <div className="orb-inner-num">$435K</div>
                <div className="orb-inner-label">Revenue captured</div>
              </div>
            </div>
            <div className="float-point fp-1">
              <div className="fp-label">Diabetes control</div>
              <div className="fp-val teal">77.9%</div>
            </div>
            <div className="float-point fp-2">
              <div className="fp-label">Missed revenue</div>
              <div className="fp-val gold">$74,400</div>
            </div>
            <div className="float-point fp-3">
              <div className="fp-label">Care gaps</div>
              <div className="fp-val">69 patients</div>
            </div>
            <div className="float-point fp-4">
              <div className="fp-label">Readmit rate</div>
              <div className="fp-val teal">12.0%</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="cy-stats">
        <div className="stats-inner">
          <div className="stat">
            <div className="stat-num">$4.6B</div>
            <div className="stat-label">Left uncaptured annually by US providers in VBC incentives</div>
          </div>
          <div className="stat">
            <div className="stat-num">67%</div>
            <div className="stat-label">Of providers can't calculate what they're owed from VBC contracts</div>
          </div>
          <div className="stat">
            <div className="stat-num">42</div>
            <div className="stat-label">Average days to manually reconcile a single VBC contract</div>
          </div>
          <div className="stat">
            <div className="stat-num">&lt;60s</div>
            <div className="stat-label">CareYield time from contract upload to payout calculation</div>
          </div>
        </div>
      </div>

      {/* MARQUEE */}
      <div className="marquee-wrap" style={{ marginTop: '80px' }}>
        <div className="marquee-inner">
          {doubled.map((item, i) => (
            <span key={i} className="marquee-item">
              <span className="marquee-dot" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* PROBLEM */}
      <section className="cy-section reveal">
        <span className="section-label">The problem</span>
        <h2 className="section-h2">
          Value-based care is a <span>revenue engine</span> — but nobody can turn the key.
        </h2>
        <div className="problem-grid">
          {[{
            n: '01', title: 'Contracts are unreadable at scale',
            desc: 'Every payer writes VBC contracts differently. Measures, thresholds, tiered payouts, exclusion rules, risk adjustment — buried in dense legal language. No two contracts are alike.'
          }, {
            n: '02', title: 'Clinical data is fragmented',
            desc: 'Patient records live across EMR systems with inconsistent coding, duplicate entries, and missing values. Answering basic population health questions requires navigating hundreds of data tables.'
          }, {
            n: '03', title: 'Attribution is a nightmare',
            desc: 'Which patients belong to which provider under which contract? Payer-specific rules, multi-provider conflicts, enrollment gaps — get it wrong and your payouts are worthless.'
          }, {
            n: '04', title: 'Revenue leaks every day',
            desc: 'Without real-time visibility into quality thresholds, care gaps go unaddressed. By the time measurement periods close, the revenue is gone — and nobody knew it was at risk.'
          }].map((c) => (
            <div key={c.n} className="problem-card">
              <div className="pc-num">{c.n}</div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SOLUTION */}
      <section className="cy-section reveal" style={{ paddingTop: 0 }}>
        <span className="section-label">The solution</span>
        <h2 className="section-h2">
          From contract to <span>cashflow</span> — fully automated.
        </h2>
        <div className="solution-content">
          <div>
            {[{
              n: '1', h: 'Contract intelligence',
              p: 'Upload any VBC contract. Our AI extracts every measure, threshold, payout tier, exclusion rule, and attribution method — structured into an executable specification.'
            }, {
              n: '2', h: 'Clinical measure engine',
              p: 'Connect your EMR. We normalize patient data, build measure-specific cohorts, apply exclusions, and calculate performance against a HEDIS-aligned measure library.'
            }, {
              n: '3', h: 'Financial reconciliation',
              p: "Every result is translated into dollars \u2014 tiered payouts, shared savings, risk adjustment, minimum sample sizes. You see what you\u2019ve earned, what you\u2019ve missed, and what\u2019s recoverable."
            }, {
              n: '4', h: 'Operational workflows',
              p: 'Care gaps are detected, prioritized by revenue impact, and routed to coordinators as actionable cases with SLAs. When gaps close, outcomes improve — and payouts increase.'
            }].map((sf) => (
              <div key={sf.n} className="sf">
                <div className="sf-num">{sf.n}</div>
                <div>
                  <h4>{sf.h}</h4>
                  <p>{sf.p}</p>
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="arch">
              <div className="arch-label">Platform architecture</div>
              <div className="arch-flow">
                <div className="arch-node an-gold">Contract PDF</div>
                <span className="arch-arrow">→</span>
                <div className="arch-node an-teal">AI extraction</div>
                <span className="arch-arrow">→</span>
                <div className="arch-node an-gold">EMR data</div>
                <span className="arch-arrow">→</span>
                <div className="arch-node an-dark">Measure calc</div>
                <span className="arch-arrow">→</span>
                <div className="arch-node an-gold">Payout report</div>
              </div>
              <div className="arch-layers">
                <div className="arch-layer">
                  <div className="al-title">Intelligence</div>
                  <div className="al-items">Contract parsing<br/>Measure library<br/>Attribution engine<br/>Gap detection</div>
                </div>
                <div className="arch-layer">
                  <div className="al-title">Data</div>
                  <div className="al-items">EMR integration<br/>Identity matching<br/>Normalization<br/>Cohort analytics</div>
                </div>
                <div className="arch-layer">
                  <div className="al-title">Operations</div>
                  <div className="al-items">Dashboards<br/>Care coordination<br/>Approval workflows<br/>Payer reconciliation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="cy-section reveal" id="outcomes" style={{ paddingTop: 0 }}>
        <span className="section-label">Real outcomes</span>
        <h2 className="section-h2">What our platform <span>delivers</span></h2>
        <div className="outcomes-grid">
          {[{
            n: '3.6x', l: 'Return on investment',
            d: 'Average ROI within the first year, driven by recovered revenue and operational efficiency gains.'
          }, {
            n: '+12%', l: 'Compliance improvement',
            d: 'Automated gap detection and care coordination close quality gaps before measurement periods end.'
          }, {
            n: '60s', l: 'Contract to payout',
            d: 'From contract upload to full payout calculation — per-measure, per-tier, per-patient detail.'
          }, {
            n: '100%', l: 'Audit-grade accuracy',
            d: 'Reproducible calculations with frozen snapshots, algorithm versioning, and SHA-256 integrity hashes.'
          }, {
            n: '5+', l: 'Payers compared',
            d: "Cross-payer heatmaps show where you\u2019re strong, where you\u2019re bleeding, and where to focus interventions."
          }, {
            n: '$0', l: 'Revenue left behind',
            d: 'Every contract clause accounted for. Every eligible patient attributed. Every dollar earned, captured.'
          }].map((o) => (
            <div key={o.n} className="outcome">
              <div className="outcome-num">{o.n}</div>
              <div className="outcome-label">{o.l}</div>
              <div className="outcome-desc">{o.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="cy-section reveal proof-section" style={{ paddingTop: 0 }}>
        <span className="section-label">Built for</span>
        <h2 className="section-h2" style={{ textAlign: 'center', maxWidth: 500, margin: '0 auto 8px' }}>
          Health systems. ACOs. <span>FQHCs.</span>
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--cy-txm)', fontSize: 15, maxWidth: 440, margin: '0 auto' }}>
          From community health centers to large provider organizations — if you have VBC contracts, CareYield tells you what you're owed.
        </p>
        <div className="proof-logos">
          {['Health systems', 'ACOs', 'FQHCs', 'IPAs', 'Clinically integrated networks'].map((l) => (
            <div key={l} className="proof-logo">{l}</div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cy-cta-section reveal">
        <span className="section-label">Get started</span>
        <h2 className="section-h2" style={{ fontSize: 48 }}>See what you're <span>owed</span>.</h2>
        <p className="cta-sub">
          Upload one VBC contract. We'll show you exactly how much revenue you're leaving on the table — and the pathway to capture it.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/contact" className="btn-primary">
            Request a demo
            <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
          <Link to="/whitepaper" className="btn-ghost">Read the whitepaper</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
